import React, { forwardRef } from 'react';
import { Button as RsButton } from 'rsuite';
import styles from './Button.module.scss';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onToggle'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

function getAppearance(variant: ButtonVariant) {
  if (variant === 'secondary') return 'default' as const;
  if (variant === 'ghost') return 'ghost' as const;
  return 'primary' as const;
}

function getColor(variant: ButtonVariant) {
  if (variant === 'danger') return 'red' as const;
  if (variant === 'success') return 'green' as const;
  return undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      children,
      className,
      disabled,
      onClick,
      type,
      ...rest
    },
    ref,
  ) => {
    return (
      <RsButton
        {...rest}
        ref={ref}
        appearance={getAppearance(variant)}
        color={getColor(variant)}
        size={size}
        loading={isLoading ? true : undefined}
        block={fullWidth ? true : undefined}
        className={className}
        disabled={disabled}
        onClick={onClick}
        type={type}
      >
        {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
        {children}
        {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
      </RsButton>
    );
  },
);

Button.displayName = 'Button';
